import { createEntityAdapter, createSlice, createSelector } from '@reduxjs/toolkit'
import { filter, get, sortBy, orderBy } from 'lodash-es'

import ActionTypes from '@constants/action_types.js'
import { getValidSortParams } from '@features/sorting/index.ts'
import { getValidFilterParams } from '@features/filtering/index.ts'
import { getSortableFieldsForTable } from '@selectors/data/field_selectors.ts'
import TimetoastTemporal from '@base/timetoast_temporal.js'

export const rowsAdapter = createEntityAdapter()

export const rowsSlice = createSlice({
  name: 'rows',
  initialState: rowsAdapter.getInitialState(),
  reducers: {
    rowRemove: rowsAdapter.removeOne,
  },
  extraReducers: builder => {
    builder.addCase(ActionTypes.RECEIVE_ENTITIES, (state, action) => {
      const { payload } = action
      if (payload.hasOwnProperty('rows')) {
        rowsAdapter.upsertMany(state, payload.rows)
      }
    })

    // Handle a field select option deletion notification by removing references
    // to the option id from row values. The action payload is expected to be
    // a normalized entities object (from jsonApiNormalize) and contain
    // `field_select_options` keyed by id.
    builder.addCase(ActionTypes.FIELD_SELECT_OPTION_DELETED, (state, action) => {
      const normalized = action.payload
      const deletedMap = normalized && normalized.field_select_options
      if (!deletedMap || !Object.keys(deletedMap).length) return

      // Build a quick map: fieldId -> Set of deleted option ids
      const deletionsByField = {}
      Object.keys(deletedMap).forEach(id => {
        const resource = deletedMap[id]
        const fieldId = resource && resource.attributes && resource.attributes.field_id
        if (!fieldId) return
        deletionsByField[fieldId] = deletionsByField[fieldId] || new Set()
        deletionsByField[fieldId].add(id.toString())
      })

      if (!Object.keys(deletionsByField).length) return

      const allRows = rowsAdapter.getSelectors().selectAll(state)
      const rowsToUpsert = []

      allRows.forEach(row => {
        const values = get(row, ['attributes', 'values']) || {}
        let mutated = false
        const newValues = { ...values }

        Object.entries(deletionsByField).forEach(([fieldId, deletedSet]) => {
          const valueKey = `field_${fieldId}`
          const currentValue = values[valueKey]
          if (currentValue == null) return

          if (typeof currentValue === 'string' || typeof currentValue === 'number') {
            if (deletedSet.has(currentValue.toString())) {
              delete newValues[valueKey]
              mutated = true
            }
          } else if (Array.isArray(currentValue)) {
            const filtered = currentValue.filter(v => v != null && !deletedSet.has(v.toString()))
            if (filtered.length !== currentValue.length) {
              if (filtered.length === 0) delete newValues[valueKey]
              else newValues[valueKey] = filtered
              mutated = true
            }
          }
        })

        if (mutated) {
          rowsToUpsert.push({ ...row, attributes: { ...row.attributes, values: newValues } })
        }
      })

      if (rowsToUpsert.length) {
        rowsAdapter.upsertMany(state, rowsToUpsert)
      }
    })
  },
})

// Selectors

export const rowsSelectors = rowsAdapter.getSelectors(state => state.data.rows)

export const getRowsForTable = createSelector(
  (_, props) => props.tableId,
  rowsSelectors.selectAll,
  function (tableId, allRows) {
    return filter(allRows, row => {
      const rowTableId = get(row, ['attributes', 'table_id'])
      return rowTableId.toString() === tableId.toString()
    })
  }
)

export const getRowsById = createSelector([getRowsForTable], rows => sortBy(rows, row => row.id))

// Resolves the row's value for a given field into something that is sortable
const resolveSortableValue = (field, value) => {
  let sortableValue = ''
  if (field.attributes.variant_type === 'TimelineField') {
    // turn timeline value into something that's sortable
    // the value is an object that looks like: { s: '2021-01-01', e: '2021-01-02' } where either s or e could be null
    const valueToParse = value ?? { s: null, e: null }
    const chosenValue = valueToParse.s ?? valueToParse.e

    // TODO: Decide what to return when have no dates
    // For now, we'll always return the max safe integer, so they always appear last
    try {
      if (chosenValue === null) {
        sortableValue = TimetoastTemporal.MS_MAX
      } else {
        const timetoastTemporal = TimetoastTemporal.parse(chosenValue)
        if (timetoastTemporal) {
          sortableValue = timetoastTemporal.getTime()
        } else {
          sortableValue = TimetoastTemporal.MS_MAX
        }
      }
    } catch (error) {
      sortableValue = TimetoastTemporal.MS_MAX
    }
  } else {
    // stringish values
    sortableValue = value ?? ''
  }
  return sortableValue
}

export const getSortedRows = createSelector(
  [getSortableFieldsForTable, getRowsById, getValidSortParams],
  (sortableFields, rows, validParams) => {
    if (validParams.length > 0) {
      const iteratee = ({ fieldId }) => {
        return row => {
          const id = `field_${fieldId}`
          const field = sortableFields.find(field => field.id === fieldId)
          const sortableFieldValue = resolveSortableValue(field, row?.attributes?.values[id])
          return sortableFieldValue
        }
      }

      const iterateeDirection = ({ direction }) => {
        return direction
      }

      const result = orderBy(rows, validParams.map(iteratee), validParams.map(iterateeDirection))
      return result
    }

    return rows
  }
)

export const getFilteredAndSortedRows = createSelector(
  [getSortedRows, getValidFilterParams],
  (rows, validFilterParams) => {
    try {
      if (validFilterParams.length > 0) {
        let updatedRows = []

        for (let i = 0; i < validFilterParams.length; i++) {
          const rowsToFilter = i === 0 ? rows : updatedRows
          const param = validFilterParams[i]

          const { fieldId, value, operator } = param
          // All filters are already validated by getValidFilterParams

          // Normalize filter values
          const filterArray = Array.isArray(value)
            ? value.map(v => (v != null ? v.toString() : v)).filter(Boolean)
            : null
          const filterString = !Array.isArray(value) ? (value ?? '').toString().toLowerCase() : null

          switch (operator) {
            case 'Contains': {
              updatedRows = rowsToFilter.filter(row => {
                const id = `field_${fieldId}`
                const fieldValue = row?.attributes?.values[id]

                // Array-based filter (e.g., multi-select)
                if (Array.isArray(filterArray)) {
                  if (Array.isArray(fieldValue)) {
                    const rowVals = fieldValue.map(v => (v != null ? v.toString() : v)).filter(Boolean)
                    return rowVals.some(v => filterArray.includes(v))
                  }
                  const single = fieldValue != null ? fieldValue.toString() : null
                  return single ? filterArray.includes(single) : false
                }

                // Text filter; coerce to string and compare case-insensitively
                const fieldStr = (
                  Array.isArray(fieldValue)
                    ? fieldValue
                        .map(v => (v != null ? v.toString() : v))
                        .filter(Boolean)
                        .join(',')
                    : (fieldValue ?? '').toString()
                ).toLowerCase()
                return fieldStr.includes(filterString)
              })
              break
            }
            case 'Excludes': {
              updatedRows = rowsToFilter.filter(row => {
                const id = `field_${fieldId}`
                const fieldValue = row?.attributes?.values[id]

                if (Array.isArray(filterArray)) {
                  if (Array.isArray(fieldValue)) {
                    const rowVals = fieldValue.map(v => (v != null ? v.toString() : v)).filter(Boolean)
                    return rowVals.every(v => !filterArray.includes(v))
                  }
                  const single = fieldValue != null ? fieldValue.toString() : null
                  // If no value on row, it doesn't include filtered values, so include it
                  return single ? !filterArray.includes(single) : true
                }

                const fieldStr = (
                  Array.isArray(fieldValue)
                    ? fieldValue
                        .map(v => (v != null ? v.toString() : v))
                        .filter(Boolean)
                        .join(',')
                    : (fieldValue ?? '').toString()
                ).toLowerCase()
                return !fieldStr.includes(filterString)
              })
              break
            }
            case 'Exactly Matches': {
              updatedRows = rowsToFilter.filter(row => {
                const id = `field_${fieldId}`
                const fieldValue = row?.attributes?.values[id]

                if (Array.isArray(filterArray)) {
                  if (Array.isArray(fieldValue)) {
                    const rowVals = fieldValue.map(v => (v != null ? v.toString() : v)).filter(Boolean)
                    if (rowVals.length !== filterArray.length) return false
                    const setA = new Set(rowVals)
                    return filterArray.every(v => setA.has(v))
                  }
                  const single = fieldValue != null ? fieldValue.toString() : null
                  return single ? filterArray.length === 1 && filterArray[0] === single : false
                }

                // For text fields, compare exactly (case-sensitive)
                const fieldStrRaw = Array.isArray(fieldValue)
                  ? fieldValue
                      .map(v => (v != null ? v.toString() : v))
                      .filter(Boolean)
                      .join(',')
                  : (fieldValue ?? '').toString()
                const filterRaw = (value ?? '').toString()
                return fieldStrRaw === filterRaw
              })
              break
            }
            default: {
              updatedRows = rowsToFilter
            }
          }
        }

        return updatedRows
      }
    } catch (error) {
      console.error('Error in getFilteredAndSortedRows:', error)
    }

    return rows
  }
)

export default rowsSlice.reducer
